import { IdesMicroWorkerMessages } from './enums/messages';
import { IWorkerMessageEvent, IWorkerMessage, IImageCoordinates } from './interfaces/message';

export class IdesMicroWorker {

	private idesMicro?: any;
	private workerGlobalContext: any;
	private frameSelection: any;

	private timeLogsEnabled?: boolean;

	constructor(workerGlobalContext: any) {
		this.workerGlobalContext = workerGlobalContext;

		this.workerGlobalContext.addEventListener('message', this.onMessage);
		this.workerGlobalContext.addEventListener('error', this.onError);
	}

	private postMessage = (id: string, message: IdesMicroWorkerMessages, data?: any): Promise<void> => new Promise((resolve) => {
		return resolve(this.workerGlobalContext.postMessage({ id, message, data } as IWorkerMessage));
	})

	private onError = (e: Error) => this.postMessage('', IdesMicroWorkerMessages.Error, { ...e });

	private onMessage = ({ data }: IWorkerMessageEvent) => {
		switch (data.message) {
			case IdesMicroWorkerMessages.PerformQualityCheck:
				return this.onPerformQualityCheck(data.id, data.data);
			case IdesMicroWorkerMessages.Terminate:
				return this.postMessage(data.id, IdesMicroWorkerMessages.Terminate)
					.then(() => this.workerGlobalContext.close());
			case IdesMicroWorkerMessages.Initialise:
				return this.onInitialise(data.data.asmScriptUrl, data.data.timeout, data.data.timeLogsEnabled)
					.then(() => this.postMessage(data.id, IdesMicroWorkerMessages.ReadyToProcess))
					.catch(() => this.postMessage(data.id, IdesMicroWorkerMessages.Error));
			default:
			// Do Nothing
		}
	}

	private onInitialise = (asmScriptUrl: string = './idesmicro_asm.js', timeout: number = 60000, timeLogsEnabled: boolean = false) => new Promise<void>((resolve, reject) => {
		var startTime = performance.now();

		this.timeLogsEnabled = timeLogsEnabled;
		if (this.timeLogsEnabled)
			console.log('smart capture time logs enabled: ' + timeLogsEnabled);

		if (this.idesMicro) {
			var endTime = performance.now();
			if (timeLogsEnabled)
				console.log(`IDES worker onInitialise method took: ${endTime - startTime} miliseconds`);
			return resolve();
		} else {
			this.workerGlobalContext.importScripts(asmScriptUrl);

			const readyTimeout = setTimeout(() => reject(), timeout);

			return this.workerGlobalContext.Initidesmicro()
				.then((idesMicro: any) => {
					clearTimeout(readyTimeout);

					this.idesMicro = idesMicro;
					this.frameSelection = new this.idesMicro.frame_selection();

					var endTime = performance.now();
					if (timeLogsEnabled)
						console.log(`IDES worker onInitialise method took: ${endTime - startTime} miliseconds`);

					resolve();
				});
		}

	})

	private onPerformQualityCheck = (id: string, { image }: { image: Uint8Array }) => {
		var startTime = performance.now();

		return this.checkQuality(image)
			.then((response) => this.postMessage(id, IdesMicroWorkerMessages.CheckComplete, response)).then(() => {
				var endTime = performance.now();
				if (this.timeLogsEnabled)
					console.log(`| onPerformQualityCheck method took: ${endTime - startTime} ms |`);
			})
			.catch((e: any) => this.postMessage(id, IdesMicroWorkerMessages.CheckFailed, e.message));
	}

	private checkQuality = (imageData: Uint8Array) =>
		this.checkImageQuality(imageData)
			.then(({ glare, blur, low_res, out_of_frame, document, done }) => {
				var startTime = performance.now();

				if (done) {
					const best_frame = this.frameSelection.get_best_frame();
					const imageData = new Uint8Array(this.idesMicro.HEAPU8.buffer, best_frame.bufferPointerAddress, best_frame.size);
					const resultImage = new Uint8Array(imageData);

					this.frameSelection.clear();

					var doneResolveResult = Promise.resolve({
						quality: {
							blurCheck: true,
							glareCheck: true,
							resolutionCheck: true,
							coordinateCheck: true
						},
						coords: [],
						image: resultImage
					});

					var endTime = performance.now();
					if (this.timeLogsEnabled)
						console.log(`| checkQuality method took: ${endTime - startTime} ms |`);

					return doneResolveResult;
				} else {
					var notDoneResolveResult = Promise.resolve({
						quality: {
							blurCheck: !blur,
							glareCheck: !glare,
							resolutionCheck: !low_res,
							coordinateCheck: !out_of_frame && document
						},
						coords: []
					});

					var endTime = performance.now();
					if (this.timeLogsEnabled)
						console.log(`| checkQuality method took: ${endTime - startTime} ms |`);

					return notDoneResolveResult;
				}
			})

	private checkImageQuality = (imageData: Uint8Array): Promise<{ glare: boolean, blur: boolean, low_res: boolean, out_of_frame: boolean, document: boolean, done: boolean }> =>
		new Promise((resolve) => {
			var startTime = performance.now();

			const numBytes = imageData.length | imageData.BYTES_PER_ELEMENT;
			const dataPtr = this.idesMicro._malloc(numBytes);
			const dataOnHeap = new Uint8Array(this.idesMicro.HEAPU8.buffer, dataPtr, numBytes);

			dataOnHeap.set(imageData);

			var addFrameStartTime = performance.now();
			const response = this.frameSelection.add_frame(dataOnHeap.byteOffset, imageData.length);
			var endTime = performance.now();
			if (this.timeLogsEnabled)
				console.log(`| add_frame method took: ${endTime - addFrameStartTime} ms |`);

			this.idesMicro._free(dataPtr);

			if (response === undefined) {
				return resolve({ glare: false, blur: false, low_res: false, out_of_frame: false, document: false, done: false });
			}

			endTime = performance.now();
			if (this.timeLogsEnabled)
				console.log(`| checkImageQuality method took: ${endTime - startTime} ms |`);

			return resolve(response);
		})
}

export default new IdesMicroWorker(self);
